import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import apiClient from '../services/api';
import { fetchObjectReadUrl } from '../utils/photoStorage';
import { PhotoViewerItem } from '../utils/photoMedia';

interface PhotoViewerModalProps {
  title?: string;
  initialPhotos?: PhotoViewerItem[];
  objetId?: string;
  startIndex?: number;
  onClose: () => void;
}

const PhotoViewerModal: React.FC<PhotoViewerModalProps> = ({
  title,
  initialPhotos = [],
  objetId,
  startIndex = 0,
  onClose,
}) => {
  const [photos, setPhotos] = useState<PhotoViewerItem[]>(initialPhotos);
  const [index, setIndex] = useState(startIndex);
  const [loading, setLoading] = useState(!!objetId);
  const [zoomed, setZoomed] = useState(false);
  const touchStartX = React.useRef<number | null>(null);

  useEffect(() => {
    setIndex(Math.min(startIndex, Math.max(0, photos.length - 1)));
  }, [startIndex, photos.length]);

  useEffect(() => {
    if (objetId) {
      let cancelled = false;
      setLoading(true);
      void (async () => {
        try {
          const response = await apiClient.get(`/objets/${objetId}/photos`, {
            params: { size: 'full' },
          });
          if (cancelled) return;
          const rows = (response.data?.photos ?? []) as PhotoViewerItem[];
          setPhotos(await resolvePhotoUrls(rows.length ? rows : initialPhotos));
        } catch {
          if (!cancelled) {
            setPhotos(await resolvePhotoUrls(initialPhotos));
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setLoading(false);
    void resolvePhotoUrls(initialPhotos).then(setPhotos);
  }, [objetId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (event.key === 'ArrowRight') setIndex((i) => Math.min(photos.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, photos.length]);

  const current = photos[index];
  const hasMultiple = photos.length > 1;

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
    setZoomed(false);
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(photos.length - 1, i + 1));
    setZoomed(false);
  }, [photos.length]);

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    const start = touchStartX.current;
    const end = event.changedTouches[0]?.clientX;
    touchStartX.current = null;
    if (start == null || end == null) return;
    const delta = end - start;
    if (Math.abs(delta) < 48) return;
    if (delta > 0) goPrev();
    else goNext();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black/95"
      role="dialog"
      aria-modal="true"
      aria-label={title ? `Photos — ${title}` : 'Visionneuse photos'}
      onClick={onClose}
    >
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 text-white shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          {title && <p className="text-sm font-medium truncate">{title}</p>}
          {hasMultiple && (
            <p className="text-xs text-white/70">
              {index + 1} / {photos.length}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setZoomed((z) => !z)}
            className="p-2 rounded-full hover:bg-white/10"
            aria-label={zoomed ? 'Réduire' : 'Agrandir'}
          >
            <ZoomIn className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10"
            aria-label="Fermer"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div
        className="relative flex-1 flex items-center justify-center px-2 pb-6 min-h-0"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {loading ? (
          <div className="text-white/70 animate-pulse">Chargement…</div>
        ) : current?.url ? (
          <img
            src={current.url}
            alt={title ? `${title} — photo ${index + 1}` : `Photo ${index + 1}`}
            className={`max-h-full max-w-full object-contain select-none transition-transform duration-200 ${
              zoomed ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'
            }`}
            onClick={() => setZoomed((z) => !z)}
            draggable={false}
          />
        ) : (
          <div className="text-white/60">Photo indisponible</div>
        )}

        {hasMultiple && index > 0 && (
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
            aria-label="Photo précédente"
          >
            <ChevronLeft className="h-7 w-7" />
          </button>
        )}
        {hasMultiple && index < photos.length - 1 && (
          <button
            type="button"
            onClick={goNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
            aria-label="Photo suivante"
          >
            <ChevronRight className="h-7 w-7" />
          </button>
        )}
      </div>

      {hasMultiple && (
        <div
          className="flex gap-2 px-4 pb-4 overflow-x-auto shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {photos.map((photo, photoIndex) => (
            <button
              key={photo.id ?? photo.s3Key ?? photoIndex}
              type="button"
              onClick={() => {
                setIndex(photoIndex);
                setZoomed(false);
              }}
              className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 ${
                photoIndex === index ? 'border-white' : 'border-transparent opacity-60'
              }`}
            >
              {photo.url ? (
                <img src={photo.url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="w-full h-full bg-white/10 block" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

async function resolvePhotoUrls(items: PhotoViewerItem[]): Promise<PhotoViewerItem[]> {
  const resolved: PhotoViewerItem[] = [];
  for (const item of items) {
    if (item.url) {
      resolved.push(item);
      continue;
    }
    if (item.s3Key) {
      const url = await fetchObjectReadUrl(item.s3Key, 'full');
      resolved.push({ ...item, url: url ?? undefined });
      continue;
    }
  }
  return resolved;
}

export default PhotoViewerModal;
